// PhoneInput — Platform-wide phone input component
//
// D-AR-FIRST + D-NUMBERS-EN-01 + D-SHADCN-LAMTIX-WRAPPED-01:
//   - Country/region-code selector beside a code-less number field
//   - Number always LTR-isolated (outer dir="ltr")
//     even when the surrounding UI is Arabic RTL (D-AR-FIRST)
//   - Latin digits only, Unicode range U+0660 to U+0669 forbidden (D-NUMBERS-EN-01)
//   - Stored as { country: string; national: string }
//   - Default: SA (+966)
//   - Sole import path @lamtix/ui (D-SHADCN-LAMTIX-WRAPPED-01)
//
// VISUAL DESIGN: adopted verbatim from Make's phone.tsx (D-UI-ADOPT-MAKE-CODE-01 #167).
//   Structure: button (flag + dialCode + ChevronDown) + portal dropdown + LTR input.
//   Wiring adaptations only:
//     - value.country is ISO code ('SA'), not dial code ('+966') — registry lookup translates.
//     - locale prop (optional, default 'ar') drives AR/EN names in the dropdown.
//     - disabled / id / name / className / maxLength preserved from prior wiring.
//     - autoComplete="tel-national" added to input for a11y.
//
// DROPDOWN PORTAL (fix — closes freeze + overflow-clipping):
//   The dropdown is rendered via createPortal(document.body) at position:fixed
//   coordinates from computeMenuPos — the same proven pattern as LxRowMenu.
//   This escapes any overflow/transform ancestor (fixes clipping), and makes the
//   backdrop truly viewport-level (closes on click → eliminates the freeze).
//   The country list is the FULL world dialing-code dataset (COUNTRY_DIAL below) with
//   a search box (filters by Arabic name, English name, dial code, or ISO code).
//
// RTL: outer wrapper has dir="ltr" — phone component is always LTR-isolated regardless of
//   page direction. Country button is always on the physical LEFT (D-PHONE-INPUT-PATTERN-01).
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { computeMenuPos } from './menu-position';

// ── Country/region registry ───────────────────────────────────────────────────
// Saudi-market focused. Fields:
//   code     — ISO 3166-1 alpha-2 (our wiring key, kept for backward compat)
//   dialCode — e.g. "+966" (used in encodePhone by useCustomerMutation / useSupplierMutation)
//   label    — legacy short label (kept for backward compat; not rendered in new UI)
//   flag     — emoji flag (Make's design)
//   labelAr  — Arabic country name (Make's design)
//   labelEn  — English country name (Make's design)
//   maxLength — national number max digits (wiring)
//
// FND-02 / NC-28 (audit): full world country list + search (the DT-CUST-PHONE-CODES deferral is
//   now closed). The hand-data is ONLY (ISO code, dial code[, maxLength]) — verifiable facts;
//   the flag is derived from the ISO code (regional-indicator codepoints) and the Arabic/English
//   names come from CLDR via Intl.DisplayNames (no hand-translated names → no hallucination).

export interface PhoneCountry {
  code: string;       // ISO 3166-1 alpha-2
  dialCode: string;   // e.g. "+966"
  label: string;      // legacy label (backward compat)
  flag: string;       // emoji flag
  labelAr: string;    // Arabic country name
  labelEn: string;    // English country name
  maxLength?: number; // national number max digits
}

// ISO alpha-2 → emoji flag via regional-indicator symbols (A→🇦 … Z→🇿). 100% derived, no table.
function isoToFlag(code: string): string {
  const cc = code.toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '🏳️';
  return String.fromCodePoint(...[...cc].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
}

// CLDR-localized region names (cached resolvers). Falls back to the EN name / code if a region
// is unavailable in the runtime's Intl data.
function makeNamer(locale: string): (code: string) => string | undefined {
  try {
    const dn = new Intl.DisplayNames([locale], { type: 'region' });
    return (code: string) => { try { return dn.of(code); } catch { return undefined; } };
  } catch { return () => undefined; }
}
const _nameAr = makeNamer('ar');
const _nameEn = makeNamer('en');

// Base registry: [ISO, dialCode, maxLength?]. Comprehensive sovereign-state coverage.
// (GCC/Arab maxLengths kept from the prior curated set; others left uncapped.)
const COUNTRY_DIAL: Array<[string, string, number?]> = [
  // GCC + Arab world
  ['SA', '+966', 9], ['AE', '+971', 9], ['KW', '+965', 8], ['QA', '+974', 8], ['BH', '+973', 8],
  ['OM', '+968', 8], ['YE', '+967'], ['JO', '+962', 9], ['LB', '+961'], ['SY', '+963'],
  ['IQ', '+964'], ['PS', '+970'], ['EG', '+20', 10], ['SD', '+249'], ['LY', '+218'],
  ['TN', '+216'], ['DZ', '+213'], ['MA', '+212'], ['MR', '+222'], ['SO', '+252'],
  ['DJ', '+253'], ['KM', '+269'],
  // Europe
  ['GB', '+44', 10], ['IE', '+353'], ['FR', '+33'], ['DE', '+49'], ['IT', '+39'], ['ES', '+34'],
  ['PT', '+351'], ['NL', '+31'], ['BE', '+32'], ['LU', '+352'], ['CH', '+41'], ['AT', '+43'],
  ['SE', '+46'], ['NO', '+47'], ['DK', '+45'], ['FI', '+358'], ['IS', '+354'], ['PL', '+48'],
  ['CZ', '+420'], ['SK', '+421'], ['HU', '+36'], ['RO', '+40'], ['BG', '+359'], ['GR', '+30'],
  ['HR', '+385'], ['SI', '+386'], ['RS', '+381'], ['BA', '+387'], ['MK', '+389'], ['ME', '+382'],
  ['AL', '+355'], ['LT', '+370'], ['LV', '+371'], ['EE', '+372'], ['BY', '+375'], ['UA', '+380'],
  ['MD', '+373'], ['MT', '+356'], ['CY', '+357'], ['RU', '+7'],
  // Asia
  ['TR', '+90'], ['IR', '+98'], ['AF', '+93'], ['PK', '+92'], ['IN', '+91'], ['BD', '+880'],
  ['LK', '+94'], ['NP', '+977'], ['BT', '+975'], ['MV', '+960'], ['CN', '+86'], ['HK', '+852'],
  ['MO', '+853'], ['TW', '+886'], ['JP', '+81'], ['KR', '+82'], ['MN', '+976'], ['ID', '+62'],
  ['MY', '+60'], ['SG', '+65'], ['TH', '+66'], ['VN', '+84'], ['PH', '+63'], ['MM', '+95'],
  ['KH', '+855'], ['LA', '+856'], ['BN', '+673'], ['KZ', '+7'], ['UZ', '+998'], ['TM', '+993'],
  ['TJ', '+992'], ['KG', '+996'], ['AZ', '+994'], ['GE', '+995'], ['AM', '+374'],
  // Africa
  ['NG', '+234'], ['GH', '+233'], ['CI', '+225'], ['SN', '+221'], ['ML', '+223'], ['NE', '+227'],
  ['BF', '+226'], ['TD', '+235'], ['CM', '+237'], ['BJ', '+229'], ['TG', '+228'], ['KE', '+254'],
  ['TZ', '+255'], ['UG', '+256'], ['RW', '+250'], ['ET', '+251'], ['ER', '+291'], ['ZA', '+27'],
  ['ZW', '+263'], ['ZM', '+260'], ['MZ', '+258'], ['AO', '+244'], ['BW', '+267'], ['NA', '+264'],
  ['MG', '+261'], ['MU', '+230'], ['GA', '+241'], ['CG', '+242'], ['CD', '+243'], ['GN', '+224'],
  // Americas
  ['US', '+1', 10], ['CA', '+1', 10], ['MX', '+52'], ['BR', '+55'], ['AR', '+54'], ['CL', '+56'],
  ['CO', '+57'], ['PE', '+51'], ['VE', '+58'], ['EC', '+593'], ['BO', '+591'], ['PY', '+595'],
  ['UY', '+598'], ['CR', '+506'], ['PA', '+507'], ['GT', '+502'], ['HN', '+504'], ['NI', '+505'],
  ['SV', '+503'], ['DO', '+1'], ['JM', '+1'], ['TT', '+1'], ['CU', '+53'], ['HT', '+509'],
  // Oceania
  ['AU', '+61'], ['NZ', '+64'], ['FJ', '+679'], ['PG', '+675'],
];

export const PHONE_COUNTRIES: PhoneCountry[] = COUNTRY_DIAL.map(([code, dialCode, maxLength]) => ({
  code,
  dialCode,
  label: `${code} ${dialCode}`,
  flag: isoToFlag(code),
  labelAr: _nameAr(code) ?? _nameEn(code) ?? code,
  labelEn: _nameEn(code) ?? code,
  ...(maxLength ? { maxLength } : {}),
}));

export const DEFAULT_PHONE_COUNTRY = 'SA';

// Dropdown width used for both the minWidth style and the computeMenuPos clamp.
const DROPDOWN_WIDTH = 240;

// ── PhoneValue ────────────────────────────────────────────────────────────────

export interface PhoneValue {
  country: string;   // ISO code, e.g. "SA"
  national: string;  // digits only, no country code, e.g. "547668888"
}

// ── Phone validation (platform-wide single rule) ────────────────────────────────
// ONE rule, applied at every place a phone number is entered: a Saudi number must be a valid
// mobile — 9 significant digits starting with 5 (05XXXXXXXX / 5XXXXXXXX); a leading trunk 0 is
// normalized first. Non-SA countries only require Latin digits. Empty is handled per `required`.
// Callers map the result to a localized message via phoneErrorMessage so the wording is consistent.
export type PhoneValidity = 'ok' | 'required' | 'digits' | 'saudi-mobile';

export function validatePhoneValue(
  phone: Pick<PhoneValue, 'country' | 'national'> | null | undefined,
  opts: { required?: boolean } = {},
): PhoneValidity {
  const national = (phone?.national ?? '').trim();
  if (!national) return opts.required ? 'required' : 'ok';
  if (!/^\d+$/.test(national)) return 'digits';
  const country = phone?.country ?? DEFAULT_PHONE_COUNTRY;
  if (country === 'SA' && !/^5\d{8}$/.test(national.replace(/^0+/, ''))) return 'saudi-mobile';
  return 'ok';
}

export function phoneErrorMessage(v: PhoneValidity, isAr: boolean): string | null {
  switch (v) {
  case 'required':     return isAr ? 'رقم الجوال مطلوب.' : 'Phone number is required.';
  case 'digits':       return isAr ? 'أرقام لاتينية فقط (0-9).' : 'Latin digits only (0-9).';
  case 'saudi-mobile': return isAr
    ? 'رقم جوال سعودي غير صحيح — يجب أن يكون 9 خانات تبدأ بـ 5 (مثال: 5XXXXXXXX).'
    : 'Invalid Saudi mobile — must be 9 digits starting with 5 (e.g. 5XXXXXXXX).';
  default:             return null;
  }
}

// ── PhoneInput ────────────────────────────────────────────────────────────────

export interface PhoneInputProps {
  value?: PhoneValue;
  onChange?: (value: PhoneValue) => void;
  disabled?: boolean;
  invalid?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  name?: string;
  locale?: 'ar' | 'en'; // drives AR/EN country names in dropdown; default 'ar' (D-AR-FIRST)
}

function PhoneInput({
  value,
  onChange,
  disabled = false,
  invalid = false,
  placeholder = '5XXXXXXXX',
  className,
  id,
  name,
  locale = 'ar',
}: PhoneInputProps) {
  const country = value?.country ?? DEFAULT_PHONE_COUNTRY;
  const national = value?.national ?? '';

  const selectedCountry = PHONE_COUNTRIES.find((c) => c.code === country) ?? PHONE_COUNTRIES[0];
  const isAr = locale === 'ar';

  // Dropdown open state + portal position + search query
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pos, setPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);

  // Filtered + sorted list: SA pinned first, then by localized name; search by name/dial/code.
  const q = query.trim().toLowerCase();
  const filteredCountries = PHONE_COUNTRIES
    .filter((c) => !q
      || c.labelAr.toLowerCase().includes(q)
      || c.labelEn.toLowerCase().includes(q)
      || c.dialCode.replace('+', '').includes(q.replace('+', ''))
      || c.code.toLowerCase().includes(q))
    .sort((a, b) => {
      if (a.code === DEFAULT_PHONE_COUNTRY) return -1;
      if (b.code === DEFAULT_PHONE_COUNTRY) return 1;
      return (isAr ? a.labelAr : a.labelEn).localeCompare(isAr ? b.labelAr : b.labelEn, isAr ? 'ar' : 'en');
    });

  function handleOpen() {
    if (disabled) return;
    if (!open && btnRef.current) {
      setQuery(''); // fresh search each open
      const rect = btnRef.current.getBoundingClientRect();
      // estimatedMenuHeight: search box + up to a few rows (the list scrolls past that)
      const estimatedMenuHeight = 300;
      setPos(
        computeMenuPos(
          rect,
          window.innerWidth,
          window.innerHeight,
          estimatedMenuHeight,
          DROPDOWN_WIDTH,
        ),
      );
    }
    setOpen((o) => !o);
  }

  // Escape key closes the dropdown
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  function handleNationalChange(e: React.ChangeEvent<HTMLInputElement>) {
    // Sanitize: Latin digits only, strip non-numeric (D-NUMBERS-EN-01)
    let sanitized = e.target.value.replace(/\D/g, '');
    // Saudi mobiles are written as 05XXXXXXXX or 5XXXXXXXX. Drop the leading trunk 0 so the
    // stored national is always the 9-digit significant number (5XXXXXXXX) that pairs with +966
    // — the user can type it either way and the maxLength cap won't clip a real digit.
    if (country === 'SA') sanitized = sanitized.replace(/^0+/, '');
    const capped = selectedCountry.maxLength
      ? sanitized.slice(0, selectedCountry.maxLength)
      : sanitized;
    onChange?.({ country, national: capped });
  }

  // ── Make's design verbatim (phone.tsx) ────────────────────────────────────
  // Outer wrapper: dir="ltr" (LTR-isolated phone component per D-PHONE-INPUT-PATTERN-01),
  // inline styles matching Make exactly. focus ring applied via onFocus/onBlur on the input.
  return (
    <div
      dir="ltr"
      className={className}
      style={{
        display: 'flex',
        background: '#fff',
        border: `1px solid ${invalid ? 'var(--lx-danger-500)' : 'var(--lx-neutral-200)'}`,
        borderRadius: 10,
        overflow: 'visible',
        position: 'relative',
        minHeight: 44,
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : undefined,
      }}
    >
      {/* Country selector button — Make's design: flag + dialCode + ChevronDown */}
      <button
        ref={btnRef}
        type="button"
        onClick={handleOpen}
        aria-label="رمز الدولة"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '0 10px',
          background: 'var(--lx-neutral-50)',
          borderInlineEnd: '1px solid var(--lx-neutral-200)',
          border: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontFamily: 'var(--lx-font-latin)',
          fontSize: 13,
          color: 'var(--lx-neutral-700)',
          minWidth: 100,
        }}
      >
        <span style={{ fontSize: 16 }}>{selectedCountry.flag}</span>
        <span style={{ fontWeight: 600 }}>{selectedCountry.dialCode}</span>
        <ChevronDown size={14} style={{ color: 'var(--lx-neutral-500)' }} />
      </button>

      {/* Code-less national number — always LTR (Make's design) */}
      <input
        id={id}
        name={name}
        dir="ltr"
        inputMode="tel"
        type="tel"
        value={national}
        onChange={handleNationalChange}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="tel-national"
        style={{
          flex: 1,
          padding: '11px 14px',
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontFamily: 'var(--lx-font-mono)',
          fontVariantNumeric: 'tabular-nums lining-nums',
          fontSize: 14,
          letterSpacing: 0.3,
          minWidth: 0,
          textAlign: 'left',
        }}
        onFocus={(e) => {
          const wrap = e.currentTarget.parentElement!;
          wrap.style.borderColor = invalid ? 'var(--lx-danger-500)' : 'var(--lx-primary-500)';
          wrap.style.boxShadow = `0 0 0 3px ${invalid ? 'var(--lx-danger-200)' : 'var(--lx-primary-200)'}`;
        }}
        onBlur={(e) => {
          const wrap = e.currentTarget.parentElement!;
          wrap.style.borderColor = invalid ? 'var(--lx-danger-500)' : 'var(--lx-neutral-200)';
          wrap.style.boxShadow = 'none';
        }}
      />

      {/* Dropdown portal — position:fixed via computeMenuPos (escapes overflow ancestors).
          Backdrop is viewport-level (position:fixed, inset:0) so click-outside reliably
          closes the dropdown — this eliminates the page-freeze from the prior
          position:absolute + in-wrapper backdrop pattern. No focus trap, no loop.
          Country list: full world dialing codes (COUNTRY_DIAL) + search. */}
      {open && typeof document !== 'undefined' && createPortal(
        <>
          {/* Backdrop: viewport-level, closes dropdown on click */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
          />
          {/* Dropdown: portal-mounted, position:fixed from computeMenuPos */}
          <div
            style={{
              position: 'fixed',
              top: pos.top,
              right: pos.right,
              zIndex: 9999,
              background: '#fff',
              border: '1px solid var(--lx-neutral-200)',
              borderRadius: 10,
              boxShadow: '0 12px 28px -10px rgba(15,20,24,0.18)',
              minWidth: DROPDOWN_WIDTH,
              maxHeight: 300,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Search box (FND-02): filter by Arabic/English name, dial code, or ISO code */}
            <div style={{ padding: 8, borderBottom: '1px solid var(--lx-neutral-100)', flexShrink: 0 }}>
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={isAr ? 'ابحث عن دولة أو مفتاح…' : 'Search country or code…'}
                dir={isAr ? 'rtl' : 'ltr'}
                style={{
                  width: '100%',
                  padding: '7px 10px',
                  border: '1px solid var(--lx-neutral-200)',
                  borderRadius: 8,
                  outline: 'none',
                  fontSize: 13,
                  fontFamily: isAr ? 'var(--lx-font-arabic)' : 'var(--lx-font-latin)',
                  color: 'var(--lx-neutral-800)',
                }}
              />
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {filteredCountries.length === 0 && (
                <div style={{ padding: '14px 12px', fontSize: 13, color: 'var(--lx-neutral-500)', textAlign: 'center', fontFamily: isAr ? 'var(--lx-font-arabic)' : 'var(--lx-font-latin)' }}>
                  {isAr ? 'لا توجد نتائج' : 'No results'}
                </div>
              )}
              {filteredCountries.map((opt) => {
                const active = opt.code === country;
                return (
                  <button
                    key={opt.code}
                    type="button"
                    onClick={() => {
                      onChange?.({ country: opt.code, national });
                      setOpen(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      width: '100%',
                      padding: '9px 12px',
                      border: 'none',
                      background: active ? 'var(--lx-primary-50)' : 'transparent',
                      color: active ? 'var(--lx-primary-700)' : 'var(--lx-neutral-700)',
                      cursor: 'pointer',
                      fontSize: 13,
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (!active) e.currentTarget.style.background = 'var(--lx-neutral-50)';
                    }}
                    onMouseLeave={(e) => {
                      if (!active) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{opt.flag}</span>
                    <span
                      style={{
                        flex: 1,
                        fontFamily: isAr ? 'var(--lx-font-arabic)' : 'var(--lx-font-latin)',
                      }}
                    >
                      {isAr ? opt.labelAr : opt.labelEn}
                    </span>
                    <span
                      style={{
                        fontFamily: 'var(--lx-font-mono)',
                        fontWeight: 600,
                        color: 'var(--lx-neutral-600)',
                      }}
                    >
                      {opt.dialCode}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

export { PhoneInput };
